import { FC, ReactNode, useMemo, useState } from 'react';

import {
  useGoogleRegister,
  useOutlookRegister,
} from 'modules/account/settings/hooks';
import { useLocation } from 'react-router-dom';
import { cn } from 'utils/helpers';
import { Button } from 'ui/components/common';
import { OutlookIcon, GmailIcon } from 'ui/components/icon';
import useModal from 'ui/partials/Modal/useModal';
import { ModalWrapper } from 'ui/partials/Modal/Modal';
import { TOKEN_SCOPE_TYPE } from 'utils/types';
import { MdClose } from 'react-icons/md';

const AddIntegrationsModal: FC<{
  scope?: TOKEN_SCOPE_TYPE;
  exclude?: string[];
}> = ({ scope = 'INBOX', exclude = [] }) => {
  const { hideModal } = useModal();
  const [chosenOption, setChosenOption] = useState<string | null>(null);

  const integrationOptions: { name: string; value: string; icon: ReactNode }[] =
    [
      { name: 'Gmail', icon: <GmailIcon />, value: 'gmail' },
      { name: 'Outlook', icon: <OutlookIcon />, value: 'outlook' },
    ].filter((val) => !exclude.includes(val.value));

  const messageToRender = useMemo(() => {
    switch (chosenOption) {
      case 'gmail':
        return (
          <GmailDialog scope={scope} onCancel={() => setChosenOption(null)} />
        );
      case 'outlook':
        return (
          <OutlookDialog scope={scope} onCancel={() => setChosenOption(null)} />
        );
    }
  }, [chosenOption, scope]);

  const mappedOptions = integrationOptions.map((option) => (
    <button
      key={option.value}
      type="button"
      onClick={() => setChosenOption(option.value)}
      className={cn(
        'group flex flex-col gap-2 justify-center items-center',
        'hover:outline outline-2 outline-primary-base transition',
        'rounded-md p-5 w-[131px] h-[129px]',
      )}
    >
      {option.icon}
      <div>{option.name}</div>
    </button>
  ));

  return (
    <div className="p-6 space-y-5 relative">
      <MdClose
        className="absolute right-5 top-5 w-5 h-5 transition hover:text-danger-base"
        role="button"
        onClick={hideModal}
      />
      {!chosenOption && (
        <div className="text-secondary-dark font-semibold text-center">
          Choose your email provider
        </div>
      )}
      <div className="flex content-center flex-wrap justify-evenly">
        {!chosenOption ? mappedOptions : messageToRender}
      </div>
    </div>
  );
};

export default AddIntegrationsModal;

const GmailDialog: FC<{ onCancel: () => void; scope?: TOKEN_SCOPE_TYPE }> = ({
  onCancel,
  scope = 'INBOX',
}) => {
  const { hideModal } = useModal();
  const { mutate: registerGoogle } = useGoogleRegister();
  const location = useLocation();

  const onClick = () => {
    registerGoogle(
      {
        returnUrl: `${location.pathname}${location.search ? location.search : ''}`,
        scope,
      },
      {
        onSuccess(data) {
          window.location.href = data;
        },
      },
    );
  };
  return (
    <ModalWrapper
      isOpen
      closeModal={hideModal}
      title="Connect your Google Account"
      content={
        <div>
          <div className="space-y-6 text-xs text-secondary-base leading-5 tracking-wider">
            <div className="leading-5">
              {`When you integrate your Google account with the Leapify platform,
  you can connect a number of Google services to your Leapify account.
  By doing so, Leapify will be able to access data and information from your Google account.
  Those services and types of data are outlined below:`}
            </div>

            <div className="ml-5 leading-5">
              <ul>
                <li className="list-disc">
                  <span className="font-semibold">Gmail: </span>
                  You can send and receive Gmail messages, schedule email sends,
                  and modify email drafts in the Leapify app. You will also be
                  able to log and track emails sent in-app. Leapify will have
                  access to your Gmail data, like email headers, subject lines,
                  and email bodies.
                </li>
              </ul>
            </div>

            <div>
              We'll only use your Google data to power different parts of our
              product that connect with the G Suite tools you use every day.
              It's your data. We just want to help you grow better.
            </div>

            <div>
              For more information on the Google integration and the types of
              data we can access, please see our Privacy Policy.
            </div>
          </div>
          <div
            className={cn(
              'flex justify-end gap-3',
              'px-2.5 pt-5 mt-5',
              'border-t-[0.5px] border-separator-primary',
            )}
          >
            <Button onClick={onClick} label="ACCEPT & CONNECT TO GOOGLE" />
            <Button onClick={onCancel} intent={'tertiary'} label="CANCEL" />
          </div>
        </div>
      }
    />
  );
};

const OutlookDialog: FC<{ onCancel: () => void; scope: TOKEN_SCOPE_TYPE }> = ({
  onCancel,
  scope = 'INBOX',
}) => {
  const { hideModal } = useModal();
  const { mutate: registerOutlook } = useOutlookRegister();
  const location = useLocation();

  const onClick = () => {
    registerOutlook(
      {
        returnUrl: `${location.pathname}${location.search ? location.search : ''}`,
        scope,
      },
      {
        onSuccess(data) {
          window.location.href = data;
        },
      },
    );
  };
  return (
    <ModalWrapper
      closeModal={hideModal}
      isOpen
      title="Connect Your Outlook Account"
      content={
        <div>
          <div className="space-y-6 text-xs text-secondary-base leading-5 tracking-wider">
            <div className="leading-5">
              {`When you integrate your Outlook account with the Leapify platform,
  you can connect a number of Outlook services to your Leapify account.
  By doing so, Leapify will be able to access data and information from your Outlook account.
  Those services and types of data are outlined below:`}
            </div>

            <div className="ml-5 leading-5">
              <ul>
                <li className="list-disc">
                  <span className="font-semibold">Outlook: </span>
                  You can send and receive Outlook messages, schedule email
                  sends, and modify email drafts in the Leapify app. You will
                  also be able to log and track emails sent in-app. Leapify will
                  have access to your Outlook data, like email headers, subject
                  lines, and email bodies.
                </li>
              </ul>
            </div>

            <div>
              We'll only use your Outlook data to power different parts of our
              product that connect with the Outlook tools you use every day.
              It's your data. We just want to help you grow better.
            </div>

            <div>
              For more information on the Outlook integration and the types of
              data we can access, please see our Privacy Policy.
            </div>
          </div>
          <div
            className={cn(
              'flex justify-end gap-3',
              'px-2.5 pt-5 mt-5',
              'border-t-[0.5px] border-separator-primary',
            )}
          >
            <Button onClick={onClick} label="ACCEPT & CONNECT TO OUTLOOK" />
            <Button onClick={onCancel} intent={'tertiary'} label="CANCEL" />
          </div>
        </div>
      }
    />
  );
};
